import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { type GUID, InternalUserProfile } from '@ndvr/auth-api';

import { AuthApi } from 'API';
import { getServiceIdentityName } from 'utils/service-identities';

export const INTERNAL_USERS_QUERY_KEY = ['internalUserProfile', 'list'] as const;

export const useInternalUserProfilesQuery = (options: { enabled?: boolean } = {}) =>
    useQuery<InternalUserProfile[]>({
        queryKey: INTERNAL_USERS_QUERY_KEY,
        enabled: options.enabled ?? true,
        queryFn: async () => {
            const [response] = await AuthApi().getActiveInternalUsers();
            return response ?? [];
        },
    });

export type InternalUserNameResolver = (internalUserGUID?: GUID<InternalUserProfile> | null) => string;

export const useInternalUserDisplayName = (): InternalUserNameResolver => {
    const { data: internalUsers } = useInternalUserProfilesQuery();
    const displayNameByGUID = useMemo(
        () => new Map((internalUsers ?? []).map((user) => [user.profileGUID, user.displayName])),
        [internalUsers],
    );
    return useCallback(
        (internalUserGUID) => (internalUserGUID && displayNameByGUID.get(internalUserGUID)) || '',
        [displayNameByGUID],
    );
};

/** What to call an actor that is neither a known actor nor a person: staff who have since left. */
export const FORMER_STAFF = 'Former staff';

/**
 * Names whoever acted on a record without looking anyone up as a person: a service identity, then active staff.
 * Returns undefined for anyone else; only staff who have since left, or a person, remain.
 */
export const useKnownActorName = () => {
    const internalUserDisplayName = useInternalUserDisplayName();
    return useCallback(
        (guid: string): string | undefined =>
            getServiceIdentityName(guid) || internalUserDisplayName(guid) || undefined,
        [internalUserDisplayName],
    );
};
